import { ViewportScroller } from "@angular/common";
import {
    AfterViewInit,
    Component,
    HostListener,
    Input,
    OnDestroy,
    OnInit,
    inject,
    ChangeDetectionStrategy
} from "@angular/core";
import moment from "moment";
import { Subject, Subscription } from "rxjs";
import { takeUntil } from "rxjs/operators";
import { BusyService } from "~app/common/components/busy/busy.service";
import { Anskaffningsformer, VirkeskopAffarsformer } from "~app/common/models/constants";
import { EventService } from "~app/common/services/cqs/event.service";
import { IBridgeEvent } from "~app/common/services/event-bridge.service";
import { DateUtils } from "~app/common/utils/date-utils";
import { ArbetsorderList } from "~app/features/planera/model/ArbetsorderList";
import { IArbetsorderAtgarder } from "~app/features/planera/model/IArbetsorderAtgarder";
import { IArbetsorderDetail } from "~app/features/planera/model/IArbetsorderDetail";
import { IGetArbetsorderRequest } from "~app/features/planera/model/IGetArbetsorderRequest";
import { IProduktgruppFilter } from "~app/features/planera/model/IProduktgruppFilter";
import { ArbetsorderListComponent } from "../arbetsorder-list/arbetsorder-list.component";
import { ArbetsorderSearchHybridService } from "../arbetsorder-search/arbetsorder-search-hybrid.service";
import { ArbetsorderOperationDeletedEvent } from "../events/ArbetsorderOperationDeletedEvent";
import { ArbetsorderOperationUpdatedEvent } from "../events/ArbetsorderOperationUpdatedEvent";
import { ListEvents } from "../events/list-events";
import { MapEvents } from "../events/map-events";
import { PlaneraEvents } from "../events/planera-events";
import { PlaneraKartaEventService } from "../planera-karta/planera-karta-event.service";
import { ProduktionsmalPlanArbetsorderSearch } from "../produktionsmal/produktionsmal-plan-arbetsordersearch.model";
import { ArbetsorderInmemoryPropertyApplierService } from "../services/arbetsorder-inmemory-property-applier.service";
import { PlaneraBridgeEvents, PlaneraEventService } from "../services/planera-event.service";
import { PlaneraService } from "../services/planera.service";
import { PlaneraSearchResultsHeaderComponent } from "./header/planera-search-results-header/planera-search-results-header.component";

@Component({
    selector: "rask-planera-search-results",
    templateUrl: "./planera-search-results.component.html",
    styleUrls: ["./planera-search-results.component.scss"],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [
        PlaneraSearchResultsHeaderComponent,
        ArbetsorderListComponent
    ]
})
export class PlaneraSearchResultsComponent implements OnInit, AfterViewInit, OnDestroy {
    @Input() active = false;
    arbetsorderList: IArbetsorderAtgarder[] = [];
    filter: IGetArbetsorderRequest;
    listHeight = 200;
    selectedArbetsorderList: IArbetsorderAtgarder[] = [];
    showOnlySelected = false;

    private readonly aoPropertyApplier = inject(ArbetsorderInmemoryPropertyApplierService);
    private readonly arbetsorderSearchHybridService = inject(ArbetsorderSearchHybridService);
    private readonly busyService = inject(BusyService);
    private readonly eventService = inject(EventService);
    private readonly ngUnsubscribe$ = new Subject<void>();
    private readonly planeraBridgeEvents: IBridgeEvent[] = [];
    private readonly planeraEventService = inject(PlaneraEventService);
    private readonly planeraKartaEventService = inject(PlaneraKartaEventService);
    private readonly planeraKartaEvents: IBridgeEvent[] = [];
    private readonly planeraService = inject(PlaneraService);
    private readonly subscriptions: Subscription[] = [];
    private readonly viewPortScroller = inject(ViewportScroller);

    handleArbetsorderDeleted(data: { arbetsorderId: number }) {
        const aoIndexInList = this.arbetsorderList.findIndex(x => x.id === data.arbetsorderId);
        if (aoIndexInList > -1) {
            this.arbetsorderList.splice(aoIndexInList, 1);
        }
    }

    handleSearchArbetsorderViaStaplar(data: ProduktionsmalPlanArbetsorderSearch) {
        this.runSearchFromStaplar(data);
    }

    ngAfterViewInit(): void {
        this.setMapHeight();
    }

    ngOnDestroy(): void {
        this.ngUnsubscribe$.next();
        this.ngUnsubscribe$.complete();
        this.subscriptions.forEach(x => x.unsubscribe());

        this.planeraEventService.unsubscribeMany(this.planeraBridgeEvents);
        this.planeraKartaEventService.unsubscribeMany(this.planeraKartaEvents);
        this.planeraBridgeEvents.splice(0);
        this.planeraKartaEvents.splice(0);
    }

    ngOnInit(): void {
        this.subscriptions.push(this.arbetsorderSearchHybridService.clearFilters
            .subscribe(x => {
                this.resetAllSearchFields();
            }));

        this.subscriptions.push(this.arbetsorderSearchHybridService.searchArbetsordrar
            .subscribe((filter: IGetArbetsorderRequest) => {
                this.searchForArbetsorder(filter);
            }));

        this.subscriptions.push(this.arbetsorderSearchHybridService.setMinaMaskinlagFilter
            .subscribe(() => {
                this.filter.maskinlag = [];
                this.arbetsorderSearchHybridService.triggerMinaMaskinlagClear();
            }));

        this.eventService.subscribeEvent<ArbetsorderOperationUpdatedEvent>(ArbetsorderOperationUpdatedEvent.$type)
            .pipe(takeUntil(this.ngUnsubscribe$))
            .subscribe(ev => {
                const arbetsorder = this.arbetsorderList.find(ao => ao.id === ev.arbetsorderId);
                if (arbetsorder) {
                    this.aoPropertyApplier.handleOperationUpdatedEvent(arbetsorder as any as IArbetsorderDetail, ev);
                }
            });

        this.eventService.subscribeEvent<ArbetsorderOperationDeletedEvent>(ArbetsorderOperationDeletedEvent.$type)
            .pipe(takeUntil(this.ngUnsubscribe$))
            .subscribe(ev => {
                const arbetsorder = this.arbetsorderList.find(ao => ao.id === ev.arbetsorderId);
                if (arbetsorder) {
                    this.aoPropertyApplier.handleOperationDeletedEvent(arbetsorder as any as IArbetsorderDetail, ev);
                }
            });

        this.planeraKartaEvents.push(this.planeraKartaEventService.subscribe(MapEvents.selectMulti, (payload: any) => {
            this.showOnlySelected = payload.selected;
        }));

        this.planeraKartaEvents.push(this.planeraKartaEventService.subscribe(ListEvents.arbetsorderListLength, (aoListLength: any) => {
            this.selectedArbetsorderList = this.arbetsorderList.filter(ao => ao.selected);
        }));

        this.planeraKartaEvents.push(this.planeraKartaEventService.subscribe(PlaneraEvents.selectChanged, (payload: any) => {
            this.selectedArbetsorderList = this.arbetsorderList.filter(ao => ao.selected);
        }));

        this.planeraBridgeEvents.push(this.planeraEventService.subscribe<ProduktionsmalPlanArbetsorderSearch>(
            PlaneraBridgeEvents.searchArbetsorderViaStaplar,
            (payload) => this.handleSearchArbetsorderViaStaplar(payload)));

        this.planeraBridgeEvents.push(this.planeraEventService.subscribe(
            PlaneraBridgeEvents.arbetsorderDeleted,
            (payload: { arbetsorderId: number }) => this.handleArbetsorderDeleted(payload)));

        this.subscriptions.push(this.arbetsorderSearchHybridService.setFilter
            .subscribe((applicapableFilter: IGetArbetsorderRequest) => {
                if (applicapableFilter) {
                    if (this.filter) {
                        this.filter.resetAllFilters();
                        Object.keys(this.filter).forEach(key => {
                            if (applicapableFilter[key]) {
                                this.filter[key] = applicapableFilter[key];
                            }
                        });
                    } else {
                        this.filter = applicapableFilter;
                    }
                    this.arbetsorderSearchHybridService.triggerFiltersUpdated();
                }
            }));

        this.setMapHeight();
    }

    @HostListener("window:resize", ["$event"]) onWindowResize(event) {
        this.setMapHeight();
    }

    resetAllSearchFields() {
        this.filter.resetAllFilters();
        this.arbetsorderSearchHybridService.triggerFiltersUpdated();
    }

    searchForArbetsorder(filter: IGetArbetsorderRequest) {
        this.planeraKartaEventService.emit(MapEvents.selectMulti, { selected: false });
        this.filter = filter;
        this.prepareGrotSkordatRange();
        this.viewPortScroller.scrollToAnchor("aoTabs");
        this.busyService.addWork(this.planeraService.searchForArbetsorder(this.filter)).subscribe(result => {
            this.arbetsorderList = result.arbetsorder.map(ao => new ArbetsorderList(ao));
            this.planeraKartaEventService.emit(MapEvents.setArbetsorderList, [...this.arbetsorderList]);
            this.arbetsorderSearchHybridService.triggerSearchResultsFound(this.arbetsorderList);
        });
    }

    setMapHeight() {
        this.listHeight = window.innerHeight - 350;
    }

    private mapProduktgrupperFilter(produktgruppIds: number[]): IProduktgruppFilter[] {
        const result: IProduktgruppFilter[] = [];

        produktgruppIds.forEach(pi => {
            result.push({
                produktgrupp: pi,
                volym: null
            });
        });

        return result;
    }

    private prepareGrotSkordatRange(): void {
        if (!this.filter.grot) {
            return;
        }
        if (this.filter.grot.skordad === false) {
            this.filter.grot.skordadFrom = null;
            this.filter.grot.skordadTo = null;
        }
    }

    private runSearchFromStaplar(data: ProduktionsmalPlanArbetsorderSearch) {
        this.resetAllSearchFields();

        const produktSatt = "Produktsatt";
        const periodSatt = "Periodsatt";

        this.filter.omraden.push({
            id: data.poId,
            typ: "PO"
        });
        this.filter.periodDateFrom = moment(data.period).startOf("month").format(DateUtils.dateformat);
        const periodEndDay = moment(data.period).endOf("month").format(DateUtils.dateformat);
        this.filter.periodDateTom = DateUtils.endOfDayDateTime(periodEndDay);
        if (data.handelssortimentId && data.planeringsstatus !== periodSatt) {
            data.handelssortimentId.forEach(x => {
                this.filter.handelssortiment.push({
                    id: x,
                    typ: "HS"
                });
            });
        }

        this.filter.virkeskop.affarsformer.push(...VirkeskopAffarsformer.Alla.filter(x => x !== VirkeskopAffarsformer.Leveransvirke));
        this.filter.anskaffningsform.valda.push(...[
            Anskaffningsformer.Egenskog,
            Anskaffningsformer.Virkeskop,
            Anskaffningsformer.CentralaVirkeskop
        ]);

        if (data.planeringsstatus === produktSatt) {
            this.filter.status.produktionsstatus.produkter.push(true);
            this.filter.periodsattPeriod = moment(data.period).format(DateUtils.dateformat);
        } else if (data.planeringsstatus === periodSatt) {
            this.filter.status.produktionsstatus.produkter.push(false);
            this.filter.periodsattPeriod = moment(data.period).format(DateUtils.dateformat);
            this.filter.skogligdata.produktgrupperBool = "or";
            this.filter.skogligdata.produktgrupper = this.mapProduktgrupperFilter(data.produktgruppIds);
        } else if (data.planeringsstatus === VirkeskopAffarsformer.Leveransvirke) {
            this.filter.virkeskop.affarsformer = [VirkeskopAffarsformer.Leveransvirke];
            this.filter.anskaffningsform.valda.splice(this.filter.anskaffningsform.valda.findIndex(x => x === Anskaffningsformer.Egenskog), 1);
        } else if (data.planeringsstatus === "Klara") {
            this.filter.status.produktionsstatus.maskinlag.push(true);
            this.filter.status.produktionsstatus.drivningspunkter.push(true);
            this.filter.status.virkesorderstatus.push("A");
            this.filter.status.produktionsstatus.produkter.push(true);
        }

        this.arbetsorderSearchHybridService.triggerFiltersUpdated();
        this.searchForArbetsorder(this.filter);
    }
}
